import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { FormText, ViewMode, FormBuilder, FormGroup } from "@coso/forms";
import { AccessRights, GroupStorage, IEntityInfo, IPermissionPairHolder, ISharePointPrivilegeLevel } from "../../../../../models/auto-generated";
import { PermissionMappingDto } from "../../../../../models/permission-mapping-dto";
import { PermissionKind, PermissionKindNames } from "../../models/PermissionKind";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

export interface PermissionMappingForm {
  accessRight: AccessRights;
  permissionLevel?: number;
  permissionLevelName?: string;
  permissions?: string[];
  entityName: string;
}

@Component({
  standalone:false,
  templateUrl: "./add-edit-permission-mapping.component.html"
})
export class AddEditPermissionMappingComponent implements OnInit {
  @ViewChild("permissionLevelPopover", { static: true }) permissionLevelPopover: TemplateRef<any>;
  @ViewChild("permissionLevelNamePopover", { static: true }) permissionLevelNamePopover: TemplateRef<any>;
  @ViewChild("entityPopover", { static: true }) entityPopover: TemplateRef<any>;

  ViewMode = ViewMode;
  GroupStorage = GroupStorage;

  errorMessage: string;
  viewMode: ViewMode;
  existingPrivilegesLevels: ISharePointPrivilegeLevel[];
  entities: IEntityInfo[];
  groupStorage: GroupStorage;
  permissionMappingToEdit: PermissionMappingDto;

  allPermissions = Array.from(PermissionKindNames.keys());
  areAllPermissionsSelected: boolean;

  formGroup: FormGroup<PermissionMappingForm>;
  formText: FormText<PermissionMappingForm>;

  saveCallback: (holder: IPermissionPairHolder) => void;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group<PermissionMappingForm>({
      accessRight: this.fb.control(AccessRights.ReadAccess, [Validators.required]),
      permissionLevel: this.fb.control(this.groupStorage !== GroupStorage.SharePoint ? undefined : this.existingPrivilegesLevels[0].id),
      permissionLevelName: this.fb.control(undefined, this.groupStorage !== GroupStorage.SharePoint ? [Validators.required] : []),
      permissions: this.fb.array([]),
      entityName: this.fb.control(null)
    });
    this.formText = {
      accessRight: {
        label: "Dynamics 365 Access Right",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: "The access right of a user in Dynamics 365.",
        options: Object.keys(AccessRights)
          .filter((r) => isNaN(Number(r)) && r !== "None")
          .map((r) => ({ label: r, value: AccessRights[r] }))
      },
      permissionLevel: {
        label: "SharePoint Permission Level",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.permissionLevelPopover,
        options: this.existingPrivilegesLevels.map((r) => ({ label: r.name, value: r.id }))
      },
      permissionLevelName: {
        label: "SharePoint Permission Level",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.permissionLevelNamePopover,
        errors: {
          required: "The SharePoint Permission Level Name field is required."
        }
      },
      entityName: {
        label: "Entity Filter",
        labelClass: "col-lg-4",
        inputContainerClass: "col-lg-6",
        popover: this.entityPopover,
        options: this.entities.map((e) => ({ label: e.displayName, value: e.logicalName }))
      }
    };

    if (this.permissionMappingToEdit) {
      this.formGroup.controls.accessRight.setValue(this.permissionMappingToEdit.accessRight);
      this.formGroup.controls.entityName.setValue(this.permissionMappingToEdit.entityName);

      if (this.permissionMappingToEdit.privilegeLevel) {
        if (this.groupStorage === GroupStorage.SharePoint) {
          const permissionLevel = this.existingPrivilegesLevels.find((e) => e.id === this.permissionMappingToEdit.privilegeLevel.id);
          this.formGroup.controls.permissionLevel.setValue(permissionLevel.id);
        } else {
          this.formGroup.controls.permissionLevelName.setValue(this.permissionMappingToEdit.privilegeLevel.name);
          const customPrivileges = this.permissionMappingToEdit.privilegeLevel.customPrivileges.split(",");
          console.log("customPrivileges", this.permissionMappingToEdit.privilegeLevel.customPrivileges);
          customPrivileges.forEach((privilege) => {
            this.formGroup.controls.permissions.push(this.fb.control(privilege));
          });
        }
      } else {
        const permissionLevel = this.existingPrivilegesLevels[0];
        this.formGroup.controls.permissionLevel.setValue(permissionLevel.id);
      }

      this.formGroup.updateValueAndValidity();
    }
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      const permissionLevel = this.groupStorage !== GroupStorage.SharePoint ? undefined : this.existingPrivilegesLevels.find((e) => e.id === this.formGroup.controls.permissionLevel.value);
      const holder: IPermissionPairHolder = {
        isCustomMapping: true,
        name: undefined,
        crM_AccessRight: this.formGroup.controls.accessRight.value,
        entityName: this.formGroup.controls.entityName.value,
        sharePoint_RoleID: permissionLevel ? permissionLevel.id : undefined,
        sharePoint_PrivilegeLevelName: permissionLevel ? permissionLevel.name : this.formGroup.controls.permissionLevelName.value,
        sharePoint_CustomPrivileges: permissionLevel ? permissionLevel.customPrivileges : this.formGroup.controls.permissions.value?.join(",")
      };
      this.saveCallback(holder);
    }
  }

  selectAll() {
    this.areAllPermissionsSelected = !this.areAllPermissionsSelected;
    if (this.areAllPermissionsSelected) {
      this.allPermissions.forEach((permission) => {
        this.formGroup.controls.permissions.push(this.fb.control(permission));
      });
    } else {
      this.formGroup.controls.permissions.clear();
    }
  }

  inputChecked(event: any) {
    if (event.target.checked) {
      this.formGroup.controls.permissions.push(this.fb.control(event.target.value));
    } else {
      const index = this.formGroup.controls.permissions.controls.findIndex((c) => c.value === event.target.value);
      if (index >= 0) {
        this.formGroup.controls.permissions.removeAt(index);
        this.areAllPermissionsSelected = false;
      }
    }
  }

  getPermissionKindName(kind: PermissionKind) {
    return PermissionKindNames.get(kind);
  }
}
